import _ from "lodash";
import { InMemoryCache } from "@apollo/client";
import update from "immutability-helper";
import { Sentry } from "services/sentry";
import { CachePersistor } from "apollo3-cache-persist";
import localForage from "localforage";
import moment from "moment";

import possibleTypes from "../possibleTypes.json";
import { dataIdFromObject, setJsonItem, populateDynamicId } from "./Utils";
import { typePolicies } from "../typePolicies";

class InMemoryCacheWrapper extends InMemoryCache {
  constructor(options) {
    super(options);
    this.errorHandler = options.errorHandler ?? _.noop;
    const onCacheUpdate = options.onCacheUpdate ?? _.noop;
    this.onCacheUpdateDebounced = _.debounce(onCacheUpdate, 1000);
  }

  write(...options) {
    const { query, result, variables = {}, dataId } = _.first(options);

    /**
     * HACK: Since apollo client has stopped generating dynamic id for non-normalized data,
     * we are generating dynamic id for non-normalized data. DO NOT REMOVE THIS CODE
     */
    const updatedResult = populateDynamicId({
      data: result,
      operation: { query, variables, dataId },
    });

    const updatedOptions = update(options, {
      0: {
        result: {
          $set: updatedResult,
        },
      },
    });

    try {
      const response = super.write(...updatedOptions);
      this.onCacheUpdateDebounced();
      return response;
    } catch (e) {
      this.errorHandler({
        error: e,
        query: _.get(query, "loc.source.body"),
        variables,
        operationName: _.get(query, "definitions[0].name.value"),
      });
    }
  }

  /**
   * We are overriding the updateQuery method to handle errors in pagination.
   */
  updateQuery(...options) {
    try {
      return super.updateQuery(...options);
    } catch (e) {
      this.errorHandler({
        error: e,
        query: _.get(options, "0.query.loc.source.body"),
        variables: _.get(options, "0.variables"),
        operationName: _.get(options, "0.query.definitions[0].name.value"),
        cacheUpdateWarnings: this?.diff({
          query: _.get(options, "0.query"),
          variables: _.get(options, "0.variables"),
        }),
      });
    }
  }
}

const cache = new InMemoryCacheWrapper({
  possibleTypes,
  dataIdFromObject,
  typePolicies,
  errorHandler: async ({
    error,
    query,
    variables,
    operationName,
    cacheUpdateWarnings,
  }) => {
    console.error(error);

    /**
     * Structure of the Error message(For non production environment):
     * 1. - "Error writing result to store for query:"
     * 2. - Entire query
     * 3. - Actual error message
     * 4. - Extra information like selection set(details of the Fragment, like what fields were specified in it)
     */

    error.message = `APOLLO-CACHE: ${
      error.message.split("\n")[2] ?? error.message
    }`;

    let updatedCacheUpdateWarnings = null;

    /**
     * Try catch is used as we are not sure if cachedData is a valid JSON or not
     */
    if (!_.isEmpty(cacheUpdateWarnings)) {
      try {
        updatedCacheUpdateWarnings = JSON.stringify(cacheUpdateWarnings);
      } catch (e) {
        updatedCacheUpdateWarnings = JSON.stringify({
          error: "Error in stringifying cachedData",
        });
      }
    }

    Sentry.withScope(scope => {
      scope.setTag("url", window.location.href);
      scope.setTag("operationName", operationName);
      // eslint-disable-next-line @toddle-edu/common/no-sentry-capture-exception
      Sentry.captureException(error, {
        extra: {
          query,
          variables: JSON.stringify(variables),
          ...(_.isEmpty(cacheUpdateWarnings)
            ? {}
            : { cacheUpdateWarnings: updatedCacheUpdateWarnings }),
        },
      });
    });

    persistor.pause();
    await persistor.purge();
  },
  onCacheUpdate: () => {
    setJsonItem({ key: "apolloCacheLastUpdatedAt", value: moment().format() });
  },
});

class CachePersistorWrapper extends CachePersistor {
  constructor(options) {
    super(options);

    /**
       * We need to handle errors of persistor.persist function, which is not exposed directky by apollo-cache-persist.
         Hence, we are grabing its reference, and wrapping it with our error handler.
        */
    this.persistor.persist = this.withStorageWriteErrorHandler(
      this.persistor.persist.bind(this.persistor)
    );

    this.purge = this.withPurgeErrorHandler(this.purge.bind(this));
  }

  withStorageWriteErrorHandler = writeFunction => {
    return async (...params) => {
      try {
        await writeFunction(...params);
      } catch (e) {
        this.pause();
        setJsonItem({ key: "stopRestoringDataFromIndexedDb", value: true });
      }
    };
  };

  withPurgeErrorHandler = purgeFunction => {
    return async (...params) => {
      try {
        await purgeFunction(...params);
      } catch (e) {
        this.pause();
        setJsonItem({
          key: "stopRestoringDataFromIndexedDb",
          value: true,
        });
      }
    };
  };
}

const persistor = new CachePersistorWrapper({
  cache,
  storage: localForage,
  serialize: false,
  maxSize: false, //No upper limit on storage size
  debug: process.env.NODE_ENV !== "production",
});

export { cache, persistor };
